import { useLocalStorage } from "usehooks-ts";

import { useOnboardingStatusData } from "@/app/data/useOnboardingStatusData";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { useGithubConnection } from "@/integration/data/useGithubConnection";
import { useLinearConnection } from "@/integration/data/useLinearConnection";
import { useSlackConnection } from "@/integration/data/useSlackConnection";

type OptionalIntegrationStep = "slack" | "github" | "linear";

type OptionalIntegrationSkips = Record<OptionalIntegrationStep, boolean>;

const defaultOptionalIntegrationSkips: OptionalIntegrationSkips = {
  slack: false,
  github: false,
  linear: false,
};

type EnvironmentActivity = {
  environmentName: string;
  timestamp: string;
  source: "server" | "client";
};

function getLatestEnvironmentActivity(
  environments: {
    name: string;
    latestSdkInitializationAt: string | null;
    latestClientSdkInitializationAt: string | null;
  }[],
): EnvironmentActivity | null {
  let latestActivity: EnvironmentActivity | null = null;

  for (const environment of environments) {
    const candidates: EnvironmentActivity[] = [];

    if (environment.latestSdkInitializationAt) {
      candidates.push({
        environmentName: environment.name,
        timestamp: environment.latestSdkInitializationAt,
        source: "server",
      });
    }

    if (environment.latestClientSdkInitializationAt) {
      candidates.push({
        environmentName: environment.name,
        timestamp: environment.latestClientSdkInitializationAt,
        source: "client",
      });
    }

    for (const candidate of candidates) {
      if (
        !latestActivity ||
        new Date(candidate.timestamp) > new Date(latestActivity.timestamp)
      ) {
        latestActivity = candidate;
      }
    }
  }

  return latestActivity;
}

export function useOnboardingProgress(options?: {
  enabled?: boolean;
  forceFullStatus?: boolean;
}) {
  const { appId, orgId } = useEnvironment();
  const isEnabled = options?.enabled !== false;
  const featureNamesQuery = useFeatureNamesData({}, { enabled: isEnabled });

  const hasFlags = (featureNamesQuery.data?.length ?? 0) > 0;
  const shouldLoadOptionalSteps =
    isEnabled &&
    (options?.forceFullStatus || !hasFlags || !featureNamesQuery.isFetched);

  const onboardingStatusQuery = useOnboardingStatusData({
    enabled: shouldLoadOptionalSteps,
    poll: shouldLoadOptionalSteps,
  });
  const slackConnectionQuery = useSlackConnection({
    enabled: shouldLoadOptionalSteps,
  });
  const githubConnectionQuery = useGithubConnection({
    enabled: shouldLoadOptionalSteps,
  });
  const linearConnectionQuery = useLinearConnection({
    enabled: shouldLoadOptionalSteps,
  });

  const [optionalIntegrationSkips, setOptionalIntegrationSkips] =
    useLocalStorage<OptionalIntegrationSkips>(
      `reflag-onboarding-optional-integrations-v1.${orgId}.${appId}`,
      defaultOptionalIntegrationSkips,
    );

  const codeIntegrationCompleted = onboardingStatusQuery.data?.hasSdkActivity;

  const slackConnected = !!slackConnectionQuery.data;
  const githubConnected = !!githubConnectionQuery.data;
  const githubRepositorySelected = !!githubConnectionQuery.data?.repository;
  const linearConnected =
    !!linearConnectionQuery.data &&
    !linearConnectionQuery.data.needsReauthorization;

  const slackCompleted = slackConnected || optionalIntegrationSkips.slack;
  const githubCompleted =
    githubRepositorySelected || optionalIntegrationSkips.github;
  const linearCompleted = linearConnected || optionalIntegrationSkips.linear;

  const createFlagCompleted = hasFlags;

  const menuCompleted = [
    !!codeIntegrationCompleted,
    slackCompleted,
    githubCompleted,
    linearCompleted,
    createFlagCompleted,
  ].filter(Boolean).length;

  const latestActivity = getLatestEnvironmentActivity(
    onboardingStatusQuery.data?.environments ?? [],
  );

  const skipOptionalStep = (step: OptionalIntegrationStep) => {
    setOptionalIntegrationSkips((current) => ({
      ...current,
      [step]: true,
    }));
  };

  const unskipOptionalStep = (step: OptionalIntegrationStep) => {
    setOptionalIntegrationSkips((current) => ({
      ...current,
      [step]: false,
    }));
  };

  return {
    hasFlags,
    menuProgress: {
      completed: menuCompleted,
      total: 5,
    },
    steps: {
      codeIntegration: {
        completed: !!codeIntegrationCompleted,
        latestActivity,
        isLoading: onboardingStatusQuery.isLoading,
        isRefetching: onboardingStatusQuery.isRefetching,
      },
      slack: {
        connected: slackConnected,
        skipped: optionalIntegrationSkips.slack,
        completed: slackCompleted,
        isLoading: slackConnectionQuery.isLoading,
      },
      github: {
        connected: githubConnected,
        configured: githubRepositorySelected,
        repository: githubConnectionQuery.data?.repository ?? null,
        skipped: optionalIntegrationSkips.github,
        completed: githubCompleted,
        isLoading: githubConnectionQuery.isLoading,
      },
      linear: {
        connected: linearConnected,
        skipped: optionalIntegrationSkips.linear,
        completed: linearCompleted,
        isLoading: linearConnectionQuery.isLoading,
      },
      createFlag: {
        completed: createFlagCompleted,
        isLoading: featureNamesQuery.isLoading,
      },
    },
    skipOptionalStep,
    unskipOptionalStep,
  };
}
